import { createContext, useContext, useEffect } from 'react';

const PortalVersionContext = createContext(undefined);

/* Classic Portal is deprecated.
   ---------------------------------------------------------------------------
   `portalVersion` is now permanently 'v2'. A migrated user is on the new
   portal regardless of what their browser has stored, and the Classic /
   New Portal toggle is gone from both navigations.

   NON-MIGRATED USERS ARE UNAFFECTED, and deliberately so. They are the
   accounts that report `v. 1.0.8` in the navigation, never had the toggle,
   and must stay on the 1.0.8 code paths. This change cannot reach them:
   App.jsx gives them a separate, smaller route table, and every dispatcher
   they can reach (Dashboard, Alerts, Reports, Users, Profile, Demo) branches
   on `dData.migrated` BEFORE it consults `isV2`, rendering the V1 component
   in the else. The dispatchers that read `isV2` without a migrated guard —
   Settings, Community, Customers, Profiles, Tasks — have no route in the
   non-migrated table at all.

   The hook and its shape are kept rather than ripped out. Sixteen callers
   still destructure `portalVersion`, their `isV2 ? <V2Page/> : <V1/>`
   ternaries still resolve correctly (always to V2), and the V1 components
   remain in use by the non-migrated else-branches. Keeping the seam also
   means a rollback is one constant, not a revert across sixteen files.
   Collapsing the ternaries is a separate cleanup, worth doing once this has
   shipped and settled. */
const PORTAL_VERSION = 'v2';

/* Both keys the toggle ever persisted under. Cleared on mount so a browser
   that still holds 'v1' does not look like it is being ignored, and so the
   stale value cannot be resurrected by a future reader. */
const LEGACY_STORAGE_KEYS = ['palcare-portal-version', 'palcare-portal-version-p2'];

/** No-op. Kept so any straggling caller cannot throw. */
const noop = () => {};

export function PortalVersionProvider({ children }) {
    useEffect(() => {
        try {
            LEGACY_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
        } catch {
            // localStorage may be unavailable; nothing here depends on it.
        }
    }, []);

    return (
        <PortalVersionContext.Provider
            value={{
                portalVersion: PORTAL_VERSION,
                togglePortalVersion: noop,
                setPortalVersion: noop,
            }}
        >
            {children}
        </PortalVersionContext.Provider>
    );
}

export function usePortalVersion() {
    const context = useContext(PortalVersionContext);
    if (context === undefined) {
        throw new Error('usePortalVersion must be used within a PortalVersionProvider');
    }
    return context;
}

export default PortalVersionContext;
